import { BooleanRadioButton, BooleanRadioButtonGroup, Callout, ExternalLink, Note } from "@octopusdeploy/design-system-components";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import type { AwsElasticContainerRegistryFeedResource, EcrOidcFeedAuthentication, FeedResource } from "@octopusdeploy/octopus-server-client";
import { isEcrOidc, FeedType } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import AdvancedFeedCredentialsFormSection, { AuthenticationType } from "~/areas/library/components/ExternalFeeds/AdvancedFeedCredentialsFormSection";
import { Summary } from "~/components/form";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import type { FeedEditProps } from "./feedTypeRegistry";
import feedTypeRegistry from "./feedTypeRegistry";
class AwsElasticContainerRegistry extends React.Component<FeedEditProps<AwsElasticContainerRegistryFeedResource>, never> {
    render() {
        if (this.props.feed.UseMachineCredentials === undefined) {
            this.props.feed.UseMachineCredentials = false;
        }
        return (<React.Fragment>
                <ExpandableFormSection errorKey="Region" title="Region" summary={this.props.feed.Region ? Summary.summary(this.props.feed.Region) : Summary.placeholder("Please enter the AWS region where this registry resides")} help="The AWS region where the registry resides." containerKey={this.props.expandableContainerKey}>
                    <Text value={this.props.feed.Region} onChange={(Region) => this.props.onChange({ ...this.props.feed, Region })} label="Region" error={this.props.getFieldError("Region")}/>
                    <Note>
                        View the <ExternalLink href="AWSRegions">AWS Regions and Endpoints</ExternalLink> documentation for a current list of the available region codes.
                    </Note>
                </ExpandableFormSection>
                <ExpandableFormSection summary={Summary.summary(this.props.feed.UseMachineCredentials ? "Use credentials configured on the worker" : "Use explicitly specified credentials")} title={"Credentials"} errorKey={"UseMachineCredentials"} help="Use explicitly specified credentials or the credentials configured on the worker." containerKey={this.props.expandableContainerKey}>
                    <BooleanRadioButtonGroup value={this.props.feed.UseMachineCredentials} onChange={(x) => {
                if (x === true) {
                    this.props.feed.SecretKey = {
                        HasValue: false,
                    };
                    this.props.feed.AccessKey = "";
                }
                this.props.feed.UseMachineCredentials = x;
                this.props.onChange(this.props.feed);
            }} title="Execute using the credentials configured on the worker">
                        <BooleanRadioButton value={true} label="Yes"/>
                        <BooleanRadioButton value={false} label="No"/>
                    </BooleanRadioButtonGroup>
                    {this.props.feed.UseMachineCredentials === true && (<Callout type={"warning"} title={"Package search and version selection on server may not resolve"}>
                            Packages will not resolve if AWS credentials are not set on this server. Please enter the full name and version during package search and release creation if no matching results are found.
                        </Callout>)}
                </ExpandableFormSection>
                {this.props.feed.UseMachineCredentials === false && (<>
                        <AdvancedFeedCredentialsFormSection model={{
                    username: this.props.feed.AccessKey,
                    password: this.props.feed.SecretKey,
                }} authenticationType={this.getAuthType(this.props.feed)} oidcModel={this.props.feed.OidcAuthentication} isNew={!this.props.feed.Id} onChanged={(model, oidcModel, authType) => {
                    if (isEcrOidc(oidcModel)) {
                        this.updateAuthentication({
                            OidcAuthentication: oidcModel,
                        });
                    }
                    else {
                        this.updateAuthentication({
                            SecretKey: model.password ?? { HasValue: false },
                            AccessKey: model.username ?? "",
                        });
                    }
                }} supportedAuthenticationTypes={[AuthenticationType.AccessKey, AuthenticationType.Oidc]} notes={<Note></Note>}/>
                    </>)}
            </React.Fragment>);
    }
    private getAuthType = (feed: AwsElasticContainerRegistryFeedResource): AuthenticationType => {
        if (feed.AccessKey)
            return AuthenticationType.AccessKey;
        if (feed.OidcAuthentication)
            return AuthenticationType.Oidc;
        return AuthenticationType.AccessKey;
    };
    private updateAuthentication = (updatedFields: Partial<AwsElasticContainerRegistryFeedResource>) => {
        const existingAuthDetails = this.props.feed;
        const updatedOidcAuthentication: EcrOidcFeedAuthentication = {
            Audience: updatedFields.OidcAuthentication?.Audience ?? existingAuthDetails.OidcAuthentication?.Audience ?? "",
            SubjectKeys: updatedFields.OidcAuthentication?.SubjectKeys ?? existingAuthDetails.OidcAuthentication?.SubjectKeys ?? [],
            RoleArn: updatedFields.OidcAuthentication?.RoleArn ?? existingAuthDetails.OidcAuthentication?.RoleArn ?? "",
        };
        this.props.onChange({ ...this.props.feed, SecretKey: updatedFields.SecretKey, AccessKey: updatedFields.AccessKey, OidcAuthentication: updatedOidcAuthentication });
    };
    static displayName = "AwsElasticContainerRegistry";
}
feedTypeRegistry.registerFeedType({
    type: FeedType.AwsElasticContainerRegistry,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    edit: AwsElasticContainerRegistry as React.ComponentType<FeedEditProps<FeedResource>>,
    text: "AWS Elastic Container Registry",
    hasUri: false,
});
